import { create, tsx } from '@dojo/framework/core/vdom';
import * as css from './Summary.m.css';
import { RenderResult } from '@dojo/framework/core/interfaces';

export interface SummaryProperties {
	/** image on right instead of left */
	reverse?: boolean;
	/** h3 instead of h2 title, smaller bottom margin */
	smaller?: boolean;
	/** unspecified = 50%/50% columns; medium = 33%/66%; large = 20%/80% */
	trailingBias?: 'medium' | 'large';
	collapse?:
		| 'hide'
		| 'stack'
		| 'stackReverse'
		| 'large'
		| 'stackReverseLarge'
		| 'xLarge'
		| 'stackReverseXLarge';
	/** false = omit bottom margin; useful for last Summary in a section */
	margin?: boolean;
	/** padding on the side of the blurb adjacent to the image */
	blurbGutter?: 'medium';
	imageAlign?: 'center' | 'top' | 'bottom';
	/** default = medium; padding between the image and the blurb */
	imageGutter?: 'medium' | 'none';
	/** if true padding will be added below the image when the widget collapses */
	stackedPadding?: boolean;
}

export interface SummaryChildren {
	image: RenderResult;
	blurb: RenderResult;
	title: RenderResult;
}

const factory = create().properties<SummaryProperties>().children<SummaryChildren>();

export const Summary = factory(function Summary({ children, properties }) {
	const [{ title, image, blurb }] = children();
	const {
		reverse,
		smaller,
		trailingBias,
		collapse = 'stack',
		margin = true,
		blurbGutter,
		imageAlign = 'center',
		imageGutter = 'medium',
		stackedPadding = true
	} = properties();

	const titleNode = smaller ? (
		<h3 classes={css.title3}>{title}</h3>
	) : (
		<h2 classes={css.title2}>{title}</h2>
	);

	let imageGutterClass: string | undefined;
	if (imageGutter === 'medium') {
		if (trailingBias) {
			imageGutterClass = reverse ? css.leftPaddingMedium : css.rightPaddingMedium;
		} else {
			imageGutterClass = css.imagePaddingMedium;
		}
	}

	return (
		<div
			classes={[
				css.root,
				margin && css.margin,
				reverse && css.reverse,
				smaller && css.smaller,
				trailingBias === 'medium' && css.trailingBias,
				trailingBias === 'large' && css.largeTrailingBias,
				collapse === 'hide' && css.collapseHide,
				(collapse === 'stack' || collapse === 'stackReverse') && css.collapseStack,
				collapse === 'large' && css.collapseStackLarge,
				collapse === 'xLarge' && css.collapseStackXLarge,
				collapse === 'stackReverse' && css.collapseStackReverse,
				collapse === 'stackReverseLarge' && css.collapseStackReverseLarge,
				collapse === 'stackReverseXLarge' && css.collapseStackReverseXLarge
			]}
		>
			<div
				classes={[
					css.leading,
					imageAlign === 'top' && css.imageTop,
					imageAlign === 'bottom' && css.imageBottom,
					imageGutterClass,
					stackedPadding && css.stackedImagePadding
				]}
			>
				{image}
			</div>
			<div
				classes={[
					css.trailing,
					blurbGutter === 'medium' &&
						(reverse ? css.rightPaddingMedium : css.leftPaddingMedium)
				]}
			>
				{titleNode}
				<div classes={css.blurb}>{blurb}</div>
			</div>
		</div>
	);
});

export default Summary;
